import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/printTemplate';
import { AssociatedCampaignsModalDialog } from 'views/UserMessaging/customModalContent/associatedCampaigns';
import { actions as userMessageActions } from 'store/userMessaging';
import { ToolbarV2, TableComponentV2 } from 'components';
import filterHelpers from 'store/helpers/filterHelpers';
import { printTemplateTableTitles } from 'constants.js';

const {
  getPaginatedPrintTemplates,
  deletePrintTemplates,
  unsetCurrentPrintTemplate,
  setCurrentPrintTemplate,
  setCopyCurrentPrintTemplate,
  getPrintTags,
  updatePrintFilters,
  resetPrintFilters
} = actions;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const PrintTemplate = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    onClickToggleArchive,
    onClickAdd,
    onClickEdit,
    onClickCopy,
    isTemplateReadOnly,
    isTenantDefaultTemplate
  } = filterHelpers();
  const action = getPaginatedPrintTemplates({ force: true });
  const templates = useSelector((store) => store.printTemplate.paginatedList);
  const filters = useSelector((store) => store.printTemplate.filters);
  const isLoading = useSelector(
    (store) => store.printTemplate.gettingPaginated
  );
  const productNames = useSelector(
    (store) => store.events.supportedEventTypes.externalProductNames
  );
  const productCategories = useSelector(
    (store) => store.events.supportedEventTypes.externalProductCategories
  );
  const tags = useSelector((store) => store.printTemplate.tagList);
  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'GLOBAL DEFAULT',
      value: 'global',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];
  const optionsToFilterProductNames = productNames
    ? productNames.map((productName) => {
        return { name: 'productName', value: productName, checked: false };
      })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories.map((productCategory) => {
        return {
          name: 'productCategory',
          value: productCategory,
          checked: false
        };
      })
    : [];
  const optionsToFilterTags = tags
    ? tags.map((tag) => {
        return { name: 'tags', value: tag, checked: false };
      })
    : [];

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    dispatch(getPaginatedPrintTemplates({ force: true }));
    dispatch(getPrintTags());
  }, []);

  const onClickArchive = (id) => {
    let action = deletePrintTemplates({
      templateIds: [id],
      activeStatus: filters?.displayActive
    });
    let warnMessage = {
      buttons: [
        { title: 'Archive Template', action: ['DISPATCH', action] },
        { title: 'Cancel', action: 'DISMISS' }
      ],
      customContent: AssociatedCampaignsModalDialog,
      props: {
        template: { associatedCampaigns: [] },
        type: 'print template'
      }
    };
    if (filters?.displayActive) {
      dispatch(userMessageActions.setMessage(warnMessage));
    } else {
      dispatch(action);
    }
  };

  const getButtonsGroup = () => [
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Templates`,
      action: () =>
        onClickToggleArchive(action, updatePrintFilters, {
          displayActive: !filters?.displayActive,
          pageNo: 0
        })
    },
    {
      name: 'Add Template',
      action: () =>
        onClickAdd(unsetCurrentPrintTemplate(), `/print-templates/details`)
    }
  ];

  const getActionButtons = (printTemplate) => [
    {
      name: isTemplateReadOnly(printTemplate, isLoading)
        ? 'View Template'
        : 'Edit Template',
      action: ['ACTION', () => onEdit(printTemplate)],
      ...(isTemplateReadOnly(printTemplate, isLoading) && {
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(printTemplate)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    },
    {
      name: 'Copy Template',
      action: ['ACTION', () => onCopy(printTemplate.id)]
    },
    {
      name: filters?.displayActive ? 'Archive Template' : 'Activate Template',
      action: ['ACTION', () => onClickArchive(printTemplate.id)],
      ...(isTemplateReadOnly(printTemplate, isLoading) && {
        isDisabled: true,
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(printTemplate)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    }
  ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterTemplate.map((option) => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: (templates) =>
          dispatch(
            updatePrintFilters({ filterTemplates: templates, pageNo: 0 })
          )
      },
      {
        options: optionsToFilterProductNames.map((option) => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: (productNames) =>
          dispatch(
            updatePrintFilters({ filterProductNames: productNames, pageNo: 0 })
          )
      },
      {
        options: optionsToFilterProductCategories.map((option) => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: (productCategories) =>
          dispatch(
            updatePrintFilters({
              filterProductCategories: productCategories,
              pageNo: 0
            })
          )
      }
    ],
    autocompleteSearch: {
      options: optionsToFilterTags.map((option) => {
        if (filters?.filterTags?.includes(option.value)) {
          option.checked = true;
        }
        return option;
      }),
      title: 'Tags',
      values: filters?.filterTags,
      onChange: (tags) =>
        dispatch(updatePrintFilters({ filterTags: tags, pageNo: 0 }))
    },
    resetFilters: () => dispatch(resetPrintFilters())
  };

  const searchOptions = {
    searchPlaceholder: 'Search Print Templates',
    searchAction: (payload) => dispatch(updatePrintFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const onEdit = (row) =>
    onClickEdit(
      [unsetCurrentPrintTemplate(), setCurrentPrintTemplate(row)],
      `/print-templates/details/${row.id}`
    );
  const onCopy = (id) =>
    onClickCopy(
      setCopyCurrentPrintTemplate,
      `/print-templates/details`,
      id,
      templates
    );

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedPrintTemplates({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={printTemplateTableTitles}
          onDoubleClickRow={(row) => onEdit(row)}
          actionButtonOptions={(row) => getActionButtons(row)}
          filteredRows={templates}
          action={getPaginatedPrintTemplates({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updatePrintFilters(payload))}
        />
      </div>
    </div>
  );
};

export default PrintTemplate;
